import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';

export default function UploadManageJobs() {
  const [jobRoles, setJobRoles] = useState([]);
  const [jobs, setJobs] = useState({});
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ jobRole: '', uploadDate: '', csvFile: null });
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleLoading, setRoleLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const controllerRef = useRef(null);
  const roleControllerRef = useRef(null);

  useEffect(() => {
    fetchJobRoles();
    return () => {
      if (controllerRef.current) controllerRef.current.abort();
      if (roleControllerRef.current) roleControllerRef.current.abort();
    };
  }, []);

  useEffect(() => {
    const formattedDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
    fetchJobsSummary(formattedDate);
  }, [currentDate]);

  const fetchJobRoles = () => {
    axios.get('/job-roles/')
      .then(res => setJobRoles(res.data))
      .catch(error => {
        console.error('Error fetching job roles:', error);
        setJobRoles([]);
      });
  };

  // Phase 1: Summary mode - only role names and counts
  const fetchJobsSummary = (formattedDate) => {
    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = new AbortController();

    setLoading(true);
    axios.get(`/jobs-by-date-and-role/?date=${formattedDate}`, {
      signal: controllerRef.current.signal
    })
      .then(res => {
        setJobs(res.data);
      })
      .catch(error => {
        if (axios.isCancel(error)) return;
        console.error('Error fetching jobs summary:', error);
        setJobs({});
      })
      .finally(() => setLoading(false));
  };

  // Phase 2: Detail mode - fetch jobs for ONE selected role
  const fetchJobsForRole = (roleName) => {
    if (roleControllerRef.current) roleControllerRef.current.abort();
    roleControllerRef.current = new AbortController();

    const formattedDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;

    setRoleLoading(true);
    axios.get(`/jobs-by-date-and-role/?date=${formattedDate}&role_name=${encodeURIComponent(roleName)}`, {
      signal: roleControllerRef.current.signal
    })
      .then(res => {
        // Merge the new detail data into the existing jobs state
        setJobs(prev => ({
          ...prev,
          ...res.data
        }));
      })
      .catch(error => {
        if (axios.isCancel(error)) return;
        console.error('Error fetching role details:', error);
      })
      .finally(() => setRoleLoading(false));
  };

  const handleRoleSelect = (roleName) => {
    setSelectedRole(roleName);
    // If we don't have jobs details for this role yet, fetch them
    if (!jobs[roleName]?.jobs || jobs[roleName].jobs.length === 0) {
      fetchJobsForRole(roleName);
    }
  };

  const handleDateChange = direction => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction === 'next' ? 1 : -1));
    setCurrentDate(newDate);
    setSelectedRole(null);
  };

  const handleFormChange = e => {
    const { name, value, files } = e.target;
    setForm(f => ({ ...f, [name]: files ? files[0] : value }));
  };

  const handleUpload = async e => {
    e.preventDefault();

    if (!form.jobRole || !form.uploadDate || !form.csvFile) {
      alert('Please fill in all fields and select a CSV file');
      return;
    }

    const formData = new FormData();
    formData.append('jobRole', form.jobRole);
    formData.append('uploadDate', form.uploadDate);
    formData.append('csv_file', form.csvFile);

    try {
      setLoading(true);
      const response = await axios.post('/upload-jobs/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      alert(response.data.message || 'Jobs uploaded successfully');
      setShowModal(false);
      setForm({ jobRole: '', uploadDate: '', csvFile: null });

      // Refresh the jobs list
      const formattedDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      fetchJobsSummary(formattedDate);
      if (selectedRole) {
        fetchJobsForRole(selectedRole);
      }

    } catch (error) {
      console.error('Error uploading jobs:', error);
      alert('Error uploading jobs: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-xl border border-slate-200/80 shadow-sm mb-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-slate-800">Job Listings</h1>
            </div>
            <p className="text-sm text-slate-500 mt-1 pl-1">
              Upload jobs via CSV and manage scraper tasks for each job role
            </p>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button 
              className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-indigo-600 transition-colors bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 px-4 py-2.5 rounded-lg w-full sm:w-auto justify-center" 
              onClick={() => window.location.href = '/manage-job-roles'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              Manage Job Roles
            </button>
            <button 
              className="flex items-center gap-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors px-4 py-2.5 rounded-lg shadow-sm w-full sm:w-auto justify-center" 
              onClick={() => setShowModal(true)}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Upload Jobs
            </button>
          </div>
        </div>

        {/* Date Navigation */}
        <div className="bg-white p-6 rounded-xl border border-slate-200/80 shadow-sm mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <button 
            className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-indigo-600 transition-all bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 px-4 py-2.5 rounded-lg active:scale-95 duration-100" 
            onClick={() => handleDateChange('previous')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Previous Day
          </button>
          <div className="flex flex-col items-center">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Active Job Listings for</span>
            <div className="flex items-center gap-3">
              <input
                type="date"
                value={`${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`}
                onChange={(e) => {
                  if (e.target.value) {
                    setCurrentDate(new Date(e.target.value));
                    setSelectedRole(null);
                  }
                }}
                className="px-4 py-2 border border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg text-sm font-bold text-slate-700 bg-white outline-none transition-all cursor-pointer shadow-inner"
              />
            </div>
          </div>
          <button 
            className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-indigo-600 transition-all bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 px-4 py-2.5 rounded-lg active:scale-95 duration-100" 
            onClick={() => handleDateChange('next')}
          >
            Next Day
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Main Content - Two Column Layout */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Sidebar - Jobs by Role */}
          <div className="w-full lg:w-1/3 bg-white rounded-xl border border-slate-200/80 shadow-sm p-6 overflow-hidden flex flex-col">
            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <span className="w-1.5 h-5 bg-indigo-600 rounded-full inline-block"></span>
              Jobs by Role
            </h2>
            {loading ? (
              <div className="flex justify-center items-center py-10">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
              </div>
            ) : Object.keys(jobs).length === 0 ? (
              <div className="text-slate-400 text-sm py-4 text-center">No jobs found for this date.</div>
            ) : (
              <>
                <div className="relative mb-4">
                  <input
                    type="text"
                    placeholder="Search job roles..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="pl-9 pr-4 py-2 border border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg text-sm w-full transition-all outline-none"
                  />
                  <div className="absolute left-3 top-2.5 text-slate-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
                {/* Summary counts */}
                <div className="flex gap-3 mb-3">
                  <span className="flex-1 text-center text-xs font-semibold bg-indigo-50 border border-indigo-100 text-indigo-700 rounded-lg py-1.5">
                    {Object.keys(jobs).length} Roles
                  </span>
                  <span className="flex-1 text-center text-xs font-semibold bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-lg py-1.5">
                    {Object.values(jobs).reduce((sum, r) => sum + (r.count || 0), 0)} Jobs
                  </span>
                </div>
                <div className="space-y-2 overflow-y-auto max-h-[500px]">
                  {Object.entries(jobs).filter(([roleName]) =>
                    roleName.toLowerCase().includes(searchTerm.toLowerCase())
                  ).length === 0 ? (
                    <div className="text-slate-400 text-sm py-4 text-center">No matching job roles found.</div>
                  ) : (
                    Object.entries(jobs)
                      .filter(([roleName]) =>
                        roleName.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                      .map(([roleName, roleData], idx) => (
                        <div
                          key={roleName}
                          className={`p-3 rounded-lg cursor-pointer transition-all border ${
                            selectedRole === roleName 
                              ? 'bg-indigo-50/80 border-indigo-200 text-indigo-900 shadow-sm' 
                              : 'bg-slate-50 hover:bg-slate-100/70 border-slate-100 hover:border-slate-200 text-slate-700'
                          }`}
                          onClick={() => handleRoleSelect(roleName)}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full shrink-0 ${
                              selectedRole === roleName
                                ? 'bg-indigo-200 text-indigo-800'
                                : 'bg-slate-200/80 text-slate-500'
                            }`}>{idx + 1}</span>
                            <span className="font-semibold text-sm flex-1 truncate">{roleName}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-bold shrink-0 ${
                              selectedRole === roleName 
                                ? 'bg-indigo-200 text-indigo-800' 
                                : 'bg-slate-200/80 text-slate-600'
                            }`}>{roleData.count} jobs</span>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </>
            )}
          </div>

          {/* Right Panel - Selected Role Jobs */}
          <div className="flex-1 bg-white rounded-xl border border-slate-200/80 shadow-sm p-6">
            {roleLoading ? (
              <div className="flex flex-col justify-center items-center py-20 gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                <p className="text-sm text-slate-400">Loading jobs for {selectedRole}...</p>
              </div>
            ) : selectedRole ? (
              jobs[selectedRole]?.jobs && jobs[selectedRole].jobs.length > 0 ? (
                <>
                  <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2 border-b border-slate-100 pb-3">
                    <span className="w-1.5 h-5 bg-indigo-600 rounded-full inline-block"></span>
                    {selectedRole} Jobs
                  </h2>
                  <div className="space-y-3 max-h-[570px] overflow-y-auto pr-1">
                    {jobs[selectedRole].jobs.map((job, index) => (
                      <div key={index} className="p-4 border border-slate-100 rounded-xl bg-white hover:bg-slate-50/50 hover:border-slate-200 shadow-sm hover:shadow transition-all duration-200">
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-2">
                          <div className="flex-1">
                            <h3 className="font-semibold text-indigo-600 text-sm hover:underline cursor-pointer" onClick={() => job.url && window.open(job.url, '_blank')}>{job.title}</h3>
                            <p className="text-sm font-medium text-slate-700 mt-1 flex items-center gap-1.5">
                              <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              </svg>
                              {job.company} 
                              <span className="text-slate-300">•</span> 
                              <span className="text-slate-500 font-normal">{job.location}</span>
                            </p>
                            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                              <svg className="w-3.5 h-3.5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                              </svg>
                              {job.yearsExpRequired}+ years experience required
                            </p>
                          </div>
                          <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start w-full sm:w-auto border-t sm:border-t-0 border-slate-50 pt-2 sm:pt-0 gap-1">
                            <span className="text-xs text-slate-400 font-medium flex items-center gap-1">
                              <svg className="w-3.5 h-3.5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {new Date(job.uploadDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full mt-1 ${
                              job.scrapingStatus === 'COMPLETED' ? 'bg-green-50 text-green-700 border border-green-100' :
                              job.scrapingStatus === 'PENDING' ? 'bg-yellow-50 text-yellow-700 border border-yellow-100' :
                                'bg-slate-50 text-slate-600 border border-slate-100'
                            }`}>
                              {job.scrapingStatus}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-20 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
                  <p className="text-slate-400 text-sm">No jobs found for this role on this date.</p>
                </div>
              )
            ) : (
              <div className="text-center py-20 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
                <svg className="w-12 h-12 text-slate-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
                <p className="text-slate-400 text-sm font-medium">
                  Select a job role from the left sidebar to view details
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 w-full max-w-md mx-4 transform transition-all duration-300 scale-100">
            <div className="flex justify-between items-center mb-5 pb-3 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Upload Jobs via CSV
              </h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1.5">Job Role</label>
                <select name="jobRole" value={form.jobRole} onChange={handleFormChange} className="border border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 px-4 py-2.5 rounded-lg w-full bg-white outline-none text-sm transition-all">
                  <option value="">Select a job role</option>
                  {jobRoles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1.5">Upload Date</label>
                <input type="date" name="uploadDate" value={form.uploadDate} onChange={handleFormChange} className="border border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 px-4 py-2.5 rounded-lg w-full outline-none text-sm transition-all" />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1.5">CSV File</label>
                <input type="file" name="csvFile" accept=".csv" onChange={handleFormChange} className="border border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 px-4 py-2.5 rounded-lg w-full outline-none text-sm transition-all file:mr-4 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100" />
                <p className="text-xs text-slate-400 mt-2 leading-relaxed">CSV must include: title, company, location, datePosted, hoursBackPosted, yearsExpRequired, url. <a href="#" className="text-indigo-600">Download sample CSV</a></p>
              </div>
              <div className="flex justify-end gap-3 pt-3 border-t border-slate-50">
                <button type="button" className="bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-semibold transition-all" onClick={() => setShowModal(false)} disabled={loading}>Cancel</button>
                <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-sm transition-all" disabled={loading}>
                  {loading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
